import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import useApi from 'hooks/useApi';

interface EscalationDefaultSettingsProps {
  teamUid: string;
  clientUID?: string;
}

type Option = { uid: string; label: string };
type ModeCfg = { enabled: boolean; actors: string[]; users: string[] };

const emptyMode = (): ModeCfg => ({ enabled: false, actors: [], users: [] });

/**
 * Process-level escalation default.
 *
 * Escalation used to be configured on every step individually, so a 20-step
 * process meant 20 identical configurations and a newly added step was silently
 * unreachable until someone remembered it. This is the one place to set it;
 * steps inherit unless explicitly overridden in the flow builder.
 *
 * Call and chat are configured separately — the people you want on a video call
 * are often not the people you want in a chat thread — and each switch is also
 * what decides whether that mode is offered to the driver at all.
 */
const EscalationDefaultSettings: React.FC<EscalationDefaultSettingsProps> = ({ teamUid, clientUID }) => {
  const { getEscalationDefault, updateEscalationDefault, getResourceTypes, getTeamUsers, showToast } = useApi();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState<Option[]>([]);
  const [users, setUsers] = useState<Option[]>([]);
  const [videoCall, setVideoCall] = useState<ModeCfg>(emptyMode());
  const [chat, setChat] = useState<ModeCfg>(emptyMode());

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const [cfgRes, rtRes, userRes] = await Promise.allSettled([
          getEscalationDefault(teamUid),
          getResourceTypes(clientUID, 'user'),
          getTeamUsers(teamUid),
        ]);
        if (!active) return;

        const rawRoles = rtRes.status === 'fulfilled' && Array.isArray(rtRes.value?.data) ? rtRes.value.data : [];
        setRoles(
          rawRoles
            .map((r: any) => ({ uid: r?.uid, label: r?.name || r?.roleName || r?.title || 'Unnamed role' }))
            .filter((r: Option) => r.uid),
        );

        // team/manage/:uid/users returns team-MAPPING rows, so the person can be
        // nested under `user` rather than being the entry itself. Handle both, or
        // the uid comes back undefined and the list silently renders empty.
        const rawUsers = userRes.status === 'fulfilled' && Array.isArray(userRes.value?.data) ? userRes.value.data : [];
        setUsers(
          rawUsers
            .map((entry: any) => {
              const u = entry?.user || entry;
              const name = `${u?.firstName || ''} ${u?.lastName || ''}`.trim();
              return { uid: u?.uid, label: name || u?.email || u?.username || u?.uid };
            })
            .filter((u: Option) => u.uid && u.label),
        );

        const cfg = cfgRes.status === 'fulfilled' ? (cfgRes.value?.data ?? cfgRes.value) : null;
        setVideoCall({ ...emptyMode(), ...(cfg?.videoCall || {}) });
        setChat({ ...emptyMode(), ...(cfg?.chat || {}) });
      } catch (e) {
        // keep defaults (off) on failure
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [teamUid, clientUID]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateEscalationDefault(teamUid, { videoCall, chat });
      showToast('Escalation default saved', 'success');
    } catch (e) {
      showToast('Failed to save escalation default', 'error');
    } finally {
      setSaving(false);
    }
  };

  const ModeSection = ({
    title,
    hint,
    cfg,
    setCfg,
  }: {
    title: string;
    hint: string;
    cfg: ModeCfg;
    setCfg: (c: ModeCfg) => void;
  }) => {
    // Naming specific people turns the role list off — mirrors the backend rule,
    // so what the admin sees here is what actually gets contacted.
    const usersWin = cfg.users.length > 0;
    return (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderRadius: 2,
            bgcolor: cfg.enabled ? 'rgba(30,142,78,0.06)' : 'grey.50',
            border: '1px solid',
            borderColor: cfg.enabled ? 'rgba(30,142,78,0.25)' : 'divider',
          }}
        >
          <Box sx={{ pr: 2 }}>
            <Typography fontWeight={600}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {hint}
            </Typography>
          </Box>
          <Switch
            checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            color="success"
          />
        </Box>

        <Collapse in={cfg.enabled} unmountOnExit>
          <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography fontWeight={600} mb={0.5}>
              Specific people
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Naming people here means only they are contacted — the roles below are then ignored.
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
              <InputLabel>Users</InputLabel>
              <Select
                multiple
                value={cfg.users}
                label="Users"
                onChange={(e) => setCfg({ ...cfg, users: e.target.value as string[] })}
                renderValue={(sel) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(sel as string[]).map((v) => (
                      <Chip key={v} label={users.find((x) => x.uid === v)?.label || v} size="small" />
                    ))}
                  </Box>
                )}
              >
                {users.length === 0 && (
                  <MenuItem disabled value="">
                    No users found on this team
                  </MenuItem>
                )}
                {users.map((u) => (
                  <MenuItem key={u.uid} value={u.uid}>
                    {u.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography fontWeight={600} mt={2} mb={0.5}>
              Or roles
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Everyone on this team holding a selected role is contacted.
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 1.5 }} disabled={usersWin}>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={cfg.actors}
                label="Roles"
                onChange={(e) => setCfg({ ...cfg, actors: e.target.value as string[] })}
                renderValue={(sel) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(sel as string[]).map((v) => (
                      <Chip key={v} label={roles.find((r) => r.uid === v)?.label || v} size="small" />
                    ))}
                  </Box>
                )}
              >
                {roles.length === 0 && (
                  <MenuItem disabled value="">
                    No user roles found for this client
                  </MenuItem>
                )}
                {roles.map((rt) => (
                  <MenuItem key={rt.uid} value={rt.uid}>
                    {rt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {usersWin && (
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                Roles are ignored while specific people are named. Clear the list above to use roles.
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, m: 2 }}>
      <CardContent>
        <Typography variant="h5" fontWeight={700} mb={0.5}>
          Escalation
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Who mobile app users reach when they escalate from a step in this process. Every step uses
          this unless it is given its own configuration in the flow builder. Call and chat can be
          switched on independently and sent to different people.
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <ModeSection
          title="Call"
          hint="Driver can start a video call from the step."
          cfg={videoCall}
          setCfg={setVideoCall}
        />
        <ModeSection
          title="Chat"
          hint="Driver can start an escalation chat thread from the step."
          cfg={chat}
          setCfg={setChat}
        />

        <Alert severity="info" sx={{ mt: 1 }}>
          A step can still be given its own escalation settings in the flow builder — open the step
          and switch it to “Configure for this step”. Steps already configured that way are not
          affected by this default.
        </Alert>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" color="secondary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EscalationDefaultSettings;
